import { useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '_store';
import { apiFetch } from 'api/client';
import { safeReturnPath } from 'login/returnPath';

export { Callback };

function Callback() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { setAuthUser, setUserRole } = useContext(AuthContext);

    useEffect(() => {
        const handleCallback = async () => {
            try {
                const code = searchParams.get('code');
                const state = searchParams.get('state');
                const error = searchParams.get('error');

                if (error) {
                    throw new Error(`Authentication failed: ${error}`);
                }

                if (!code) {
                    throw new Error('No authorization code received');
                }

                // Exchange authorization code for tokens via your API
                const response = await apiFetch('/authenticate', {
                    method: 'POST',
                    json: { code, state, authMethod: 'authentik', browser_session: true },
                    skipSessionCheck: true,
                });

                if (!response.ok) {
                    throw new Error('Token exchange failed');
                }

                const data = await response.json();
                const { role, roles } = data;

                // Update auth state
                setAuthUser(data);
                setUserRole(role || roles || 'user');

                // Parse state to get return URL
                let returnUrl = '/';
                if (state) {
                    try {
                        let stateData;
                        try {
                            stateData = JSON.parse(state);
                        } catch {
                            stateData = JSON.parse(decodeURIComponent(state));
                        }
                        returnUrl = safeReturnPath(stateData.returnUrl);
                    } catch (e) {
                        console.warn('Could not parse state parameter');
                    }
                }

                // Redirect to the intended page
                navigate(returnUrl);
            } catch (error) {
                console.error('Callback error:', error);
                navigate('/login?error=' + encodeURIComponent(error.message));
            }
        };

        handleCallback();
    }, [searchParams, navigate, setAuthUser, setUserRole]);

    return (
        <div className="flex justify-center items-center h-screen">
            <div className="text-center">
                <span className="inline-block animate-spin rounded-full h-8 w-8 border-4" style={{ borderColor: 'var(--color-loading-spinner)', borderTopColor: 'transparent' }} role="status">
                    <span className="sr-only">Processing authentication...</span>
                </span>
                <p className="mt-3">Completing authentication...</p>
            </div>
        </div>
    );
}
